// =============================================================================
//  api_subscriptions.ts
//
//  Standalone subscription module for AiSchoolCoach. Houses:
//    - Subscription-related TypeScript types
//    - SubscriptionService class (Stripe checkout, free tier, cancellation,
//      updates, billing history, webhooks, cohort enrollment)
//    - Plan and status constants
//    - The shared `subscriptionService` instance
//
//  Depends on three helpers exported by ./api:
//    - API_BASE_URL     (Vite-driven base URL)
//    - getAuthHeaders() (adds Authorization bearer token)
//    - handleResponse() (parses JSON / throws on non-2xx)
//
//  Note (bug fix from api.js): the original JS exported `subscribeFreeTier`
//  but SubscriptionPlans.js called `subscribeFreetier`. This module
//  standardizes on `subscribeFreeTier` (capital T). Update any callers.
// =============================================================================

import { API_BASE_URL, getAuthHeaders, handleResponse } from './api';
import type { APIResponse } from '../types';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type BillingInterval = 'monthly' | 'annually';

export type SubscriptionStatusType =
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing';

export interface CheckoutSessionResponse {
  sessionId: string;
  url?: string;
}

export interface Subscription {
  id: string;
  userId: string;
  planId: string;
  status: SubscriptionStatusType;
  billingInterval: BillingInterval;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionPlanRecord {
  id: string;
  name: string;
  description: string;
  monthlyPriceId: string;
  annualPriceId: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
}

export interface BillingHistoryItem {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'failed' | 'pending' | 'refunded';
  invoiceUrl?: string;
  description?: string;
}

export interface CohortEnrollment {
  id: string;
  title: string;
}

export interface StripeWebhookEvent {
  signature: string;
  data: unknown;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

export const SUBSCRIPTION_PLANS = {
  FREE: 'free_tier',
  BASIC: 'basic_tier',
  PREMIUM: 'premium_tier',
  ELITE: 'elite_tier',
  // AiSchoolCoach tiers
  AI_SPARK: 'schoolcoach_spark',
  AI_BUILDER: 'schoolcoach_builder',
  AI_FOUNDER: 'schoolcoach_founder',
  // Elementary tiers
  ELEM_SPARK: 'elem_spark',
  ELEM_BUILDER: 'elem_builder',
  ELEM_FOUNDER: 'elem_founder',
} as const;

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid',
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  TRIALING: 'trialing',
} as const;

export type SubscriptionPlanId =
  (typeof SUBSCRIPTION_PLANS)[keyof typeof SUBSCRIPTION_PLANS];

export type ModuleId = 'budget' | 'food_truck' | 'retirement' | 'tax' | 'credit_assessment' | 'prv_prospecting';

export const ALL_MODULES: ModuleId[] = [
  'budget',
  'food_truck',
  'retirement',
  'tax',
  'credit_assessment',
  'prv_prospecting',
];

export const MODULE_ACCESS_BY_PLAN: Record<string, ModuleId[]> = {
  [SUBSCRIPTION_PLANS.AI_SPARK]:   ['budget'],
  [SUBSCRIPTION_PLANS.AI_BUILDER]: ['budget', 'food_truck'],
  [SUBSCRIPTION_PLANS.AI_FOUNDER]: ALL_MODULES,
};

export type ElementaryItemId = 'allowance_savings' | 'charitable_giving' | 'goal_setting' | 'lemonade_stand' | 'wants_needs';

export const ALL_ELEMENTARY_ITEMS: ElementaryItemId[] = [
  'allowance_savings',
  'charitable_giving',
  'goal_setting',
  'lemonade_stand',
  'wants_needs',
];

export const ELEMENTARY_ACCESS_BY_PLAN: Record<string, ElementaryItemId[]> = {
  [SUBSCRIPTION_PLANS.ELEM_SPARK]:   ['allowance_savings'],
  [SUBSCRIPTION_PLANS.ELEM_BUILDER]: ['allowance_savings', 'charitable_giving', 'goal_setting'],
  [SUBSCRIPTION_PLANS.ELEM_FOUNDER]: ALL_ELEMENTARY_ITEMS,
};

// -----------------------------------------------------------------------------
// Service
// -----------------------------------------------------------------------------

export class SubscriptionService {
  /**
   * Create a Stripe Checkout Session and return a sessionId for client-side
   * redirect via stripe.redirectToCheckout({ sessionId }).
   */
  async createCheckoutSession(
    priceId: string,
    userId: string,
    userEmail: string,
    planId: string,
    billingInterval: BillingInterval,
    locale: string = 'en',
    metadata: Record<string, unknown> = {}
  ): Promise<APIResponse<CheckoutSessionResponse>> {
    try {
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          priceId,
          userId,
          userEmail,
          planId,
          billingInterval,
          locale,
          metadata,
        }),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Checkout session created',
      };
    } catch (error) {
      console.error('Create checkout session error:', error);
      throw error;
    }
  }

  /**
   * Subscribe a user to the free tier without going through Stripe.
   */
  async subscribeFreeTier(
    userId: string,
    planId: string
  ): Promise<APIResponse<Subscription>> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/free-tier`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId, planId }),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Free tier subscription created',
      };
    } catch (error) {
      console.error('Free tier subscription error:', error);
      throw error;
    }
  }

  async getCurrentSubscription(
    userId: string
  ): Promise<APIResponse<Subscription | null>> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/${userId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription retrieved',
      };
    } catch (error) {
      console.error('Get subscription error:', error);
      throw error;
    }
  }

  async cancelSubscription(
    subscriptionId: string
  ): Promise<APIResponse<Subscription>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscriptions/${subscriptionId}/cancel`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription canceled',
      };
    } catch (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }
  }

  async updateSubscription(
    subscriptionId: string,
    updateData: Partial<Subscription>
  ): Promise<APIResponse<Subscription>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscriptions/${subscriptionId}`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData),
        }
      );
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription updated',
      };
    } catch (error) {
      console.error('Update subscription error:', error);
      throw error;
    }
  }

  async getSubscriptionPlans(): Promise<APIResponse<SubscriptionPlanRecord[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription-plans`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription plans retrieved',
      };
    } catch (error) {
      console.error('Get subscription plans error:', error);
      throw error;
    }
  }

  async getBillingHistory(
    userId: string
  ): Promise<APIResponse<BillingHistoryItem[]>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscriptions/${userId}/billing-history`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Billing history retrieved',
      };
    } catch (error) {
      console.error('Get billing history error:', error);
      throw error;
    }
  }

  async handleWebhookEvent(
    event: StripeWebhookEvent
  ): Promise<APIResponse<unknown>> {
    try {
      const response = await fetch(`${API_BASE_URL}/webhooks/stripe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Stripe-Signature': event.signature,
        },
        body: JSON.stringify(event.data),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Webhook processed',
      };
    } catch (error) {
      console.error('Webhook handling error:', error);
      throw error;
    }
  }

  /**
   * Enroll a user in a free cohort. Returns true on success.
   */
  async enrollInCohort(cohort: CohortEnrollment): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/cohort-enrollment`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          cohort_id: cohort.id,
          cohort_name: cohort.title,
          price: 0,
          payment_id: 'free_enrollment',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to enroll in free cohort');
      }
      return true;
    } catch (error) {
      console.error('Error enrolling in free cohort:', error);
      throw error;
    }
  }
}

// -----------------------------------------------------------------------------
// Shared instance
// -----------------------------------------------------------------------------

export const subscriptionService = new SubscriptionService();

export default subscriptionService;
